import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { GeneralModal } from 'ui/GeneralModal';
import { Multiselect, IMultiselectValueLabelPair } from 'ui/Multiselect';
import { TextInput } from 'ui/TextInput';
import FluidButton from 'ui/FluidButton';
import { enqueueNotification } from 'store/modules/ui';
import { useExchangeRateQuery } from 'containers/BankTransactions/hooks/useExchangeRateQuery';
import { useCreateConversionPairMutation, useUpdateConversionPairMutation, ICreateConversionPairInput } from './hooks/useConversionRatesQueries';
import { EConversionStrategy, EExchangeRateSource, ISystemExchangeRateData } from './types';
import { TCurrencyCode } from 'interfaces';
import currencies from 'config/data/currencies.json';
import ActivityIcon from 'ui/Icons/tabler/activity.component.svg';

interface IAddConversionPairModalProps {
  onClose: () => void;
  editData?: ISystemExchangeRateData;
}

const currencyOptions: IMultiselectValueLabelPair[] = Object.keys(currencies).map(code => ({
  value: code,
  label: `${code} - ${(currencies as Record<string, { name: string }>)[code].name}`,
}));

const strategyOptions: IMultiselectValueLabelPair[] = [
  { value: 'api_rate', label: 'API Rate' },
  { value: 'fixed_rate', label: 'Fixed Rate' },
  { value: 'markup_percentage', label: 'Markup %' },
];

interface IFormErrors {
  fromCurrency?: string;
  toCurrency?: string;
  buyStrategy?: string;
  buyValue?: string;
  sellStrategy?: string;
  sellValue?: string;
}

const mapSourceToStrategy = (source: EExchangeRateSource, markupPercentage: number): EConversionStrategy => {
  if (source === 'fixed') return 'fixed_rate';
  if (markupPercentage !== 0) return 'markup_percentage';
  return 'api_rate';
};

const getValueForStrategy = (strategy: EConversionStrategy, fixedValue: number, markupPercentage: number): string => {
  if (strategy === 'fixed_rate') return String(fixedValue);
  if (strategy === 'markup_percentage') return String(markupPercentage);
  return '';
};

const calculateEffectiveRate = (
  strategy: EConversionStrategy | null,
  value: string,
  apiRate: number | undefined
): string => {
  if (!strategy) return '---';
  if (strategy === 'api_rate') {
    return apiRate != null ? String(apiRate) : '---';
  }
  if (strategy === 'fixed_rate') {
    return value !== '' ? value : '---';
  }
  if (strategy === 'markup_percentage') {
    if (apiRate != null && value !== '') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        const effective = (apiRate * (100 + numValue)) / 100;
        return String(effective);
      }
    }
    return '---';
  }
  return '---';
};

export const AddConversionPairModal = ({ onClose, editData }: IAddConversionPairModalProps) => {
  const dispatch = useDispatch();
  const isEditMode = !!editData;

  const initialBuyStrategy = editData ? mapSourceToStrategy(editData.buySource, editData.buyMarkupPercentage) : null;
  const initialSellStrategy = editData ? mapSourceToStrategy(editData.sellSource, editData.sellMarkupPercentage) : null;

  const [fromCurrency, setFromCurrency] = useState<TCurrencyCode | null>(editData?.from ?? null);
  const [toCurrency, setToCurrency] = useState<TCurrencyCode | null>(editData?.to ?? null);
  const [buyStrategy, setBuyStrategy] = useState<EConversionStrategy | null>(initialBuyStrategy);
  const [buyValue, setBuyValue] = useState(
    initialBuyStrategy
      ? getValueForStrategy(initialBuyStrategy, editData?.buy ?? 0, editData?.buyMarkupPercentage ?? 0)
      : ''
  );
  const [sellStrategy, setSellStrategy] = useState<EConversionStrategy | null>(initialSellStrategy);
  const [sellValue, setSellValue] = useState(
    initialSellStrategy
      ? getValueForStrategy(initialSellStrategy, editData?.sell ?? 0, editData?.sellMarkupPercentage ?? 0)
      : ''
  );
  const [errors, setErrors] = useState<IFormErrors>({});

  const bothCurrenciesSelected = !!fromCurrency && !!toCurrency;
  const sameCurrencyError = !isEditMode && bothCurrenciesSelected && fromCurrency === toCurrency;
  const hasDuplicatePairError = errors.fromCurrency === 'This currency pair already exist';
  const hasConversionError = sameCurrencyError || hasDuplicatePairError;

  const { data: fetchedApiRate } = useExchangeRateQuery(
    fromCurrency,
    toCurrency,
    !isEditMode && bothCurrenciesSelected && !sameCurrencyError
  );

  const apiRate = isEditMode ? editData.api : fetchedApiRate;

  const createMutation = useCreateConversionPairMutation();
  const updateMutation = useUpdateConversionPairMutation();
  const activeMutation = isEditMode ? updateMutation : createMutation;

  const buyEffectiveRate = useMemo(() => calculateEffectiveRate(buyStrategy, buyValue, apiRate), [
    buyStrategy,
    buyValue,
    apiRate,
  ]);

  const sellEffectiveRate = useMemo(() => calculateEffectiveRate(sellStrategy, sellValue, apiRate), [
    sellStrategy,
    sellValue,
    apiRate,
  ]);

  const showSuccessNotification = useCallback(() => {
    const message = isEditMode ? 'Conversion pair updated successfully' : 'Currency pair saved successfully.';
    dispatch(enqueueNotification({ message, options: { variant: 'success' } }));
  }, [dispatch, isEditMode]);

  const showErrorNotification = useCallback(
    (message: string) => {
      dispatch(enqueueNotification({ message, options: { variant: 'error' } }));
    },
    [dispatch]
  );

  const validate = useCallback((): IFormErrors => {
    const newErrors: IFormErrors = {};
    if (!isEditMode) {
      if (!fromCurrency) newErrors.fromCurrency = 'Field required';
      if (!toCurrency) newErrors.toCurrency = 'Field required';
      if (sameCurrencyError) newErrors.toCurrency = 'Currencies should be different';
    }
    if (!buyStrategy) newErrors.buyStrategy = 'Field required';
    if (buyStrategy && buyStrategy !== 'api_rate') {
      if (buyValue === '') {
        newErrors.buyValue = 'Field required';
      } else if (isNaN(Number(buyValue))) {
        newErrors.buyValue = 'Value must be a valid number';
      } else {
        const decimals = buyValue.includes('.') ? buyValue.split('.')[1].length : 0;
        if (decimals > 15) newErrors.buyValue = 'Maximum 15 decimal places allowed';
      }
    }
    if (!sellStrategy) newErrors.sellStrategy = 'Field required';
    if (sellStrategy && sellStrategy !== 'api_rate') {
      if (sellValue === '') {
        newErrors.sellValue = 'Field required';
      } else if (isNaN(Number(sellValue))) {
        newErrors.sellValue = 'Value must be a valid number';
      } else {
        const decimals = sellValue.includes('.') ? sellValue.split('.')[1].length : 0;
        if (decimals > 15) newErrors.sellValue = 'Maximum 15 decimal places allowed';
      }
    }
    return newErrors;
  }, [isEditMode, fromCurrency, toCurrency, sameCurrencyError, buyStrategy, buyValue, sellStrategy, sellValue]);

  const handleSave = useCallback(async () => {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const input: ICreateConversionPairInput = {
      fromCurrency: fromCurrency!,
      toCurrency: toCurrency!,
      buyStrategy: buyStrategy!,
      buyValue: buyStrategy === 'api_rate' ? null : parseFloat(buyValue),
      sellStrategy: sellStrategy!,
      sellValue: sellStrategy === 'api_rate' ? null : parseFloat(sellValue),
    };

    try {
      await activeMutation.mutateAsync(input);
      showSuccessNotification();
      onClose();
    } catch (error) {
      const status = error?.response?.status;
      const errorMessage = error?.response?.data?.errors?.message || error?.response?.data?.message || '';
      const isSameCurrency = errorMessage.includes('must be different');
      if (isSameCurrency) {
        setErrors(prev => ({ ...prev, toCurrency: 'Currencies should be different' }));
      } else if (status === 409) {
        setErrors(prev => ({ ...prev, fromCurrency: 'This currency pair already exist' }));
      } else if (status === 404) {
        showErrorNotification('This currency pair no longer exists. It may have been deleted.');
      } else {
        showErrorNotification('Failed to save currency pair. Please try again.');
      }
    }
  }, [
    validate,
    activeMutation,
    fromCurrency,
    toCurrency,
    buyStrategy,
    buyValue,
    sellStrategy,
    sellValue,
    showSuccessNotification,
    showErrorNotification,
    onClose,
  ]);

  const handleFromCurrencyChange = useCallback((values: string[]) => {
    const val = values[0] as TCurrencyCode | undefined;
    setFromCurrency(val ?? null);
    setErrors(prev => ({ ...prev, fromCurrency: undefined }));
  }, []);

  const handleToCurrencyChange = useCallback((values: string[]) => {
    const val = values[0] as TCurrencyCode | undefined;
    setToCurrency(val ?? null);
    setErrors(prev => ({ ...prev, toCurrency: undefined, fromCurrency: undefined }));
  }, []);

  const handleBuyStrategyChange = useCallback((values: string[]) => {
    const val = values[0] as EConversionStrategy | undefined;
    setBuyStrategy(val ?? null);
    if (val === 'api_rate') setBuyValue('');
    setErrors(prev => ({ ...prev, buyStrategy: undefined, buyValue: undefined }));
  }, []);

  const handleSellStrategyChange = useCallback((values: string[]) => {
    const val = values[0] as EConversionStrategy | undefined;
    setSellStrategy(val ?? null);
    if (val === 'api_rate') setSellValue('');
    setErrors(prev => ({ ...prev, sellStrategy: undefined, sellValue: undefined }));
  }, []);

  return (
    <GeneralModal
      onClose={onClose}
      modalWindowClassName="w-[600px] px-[35px] py-[25px] rounded"
      shouldCloseByClickingOutside={false}
    >
      <span className="block font-noe-display text-[21px] text-black">
        {isEditMode ? 'Edit Currency Pair' : 'New Currency Pair'}
      </span>

      {/* CONVERSION Section */}
      <fieldset
        className={`border rounded px-[15px] pt-[10px] pb-[20px] mt-[25px] ${
          hasConversionError ? 'border-red-95' : 'border-gray-20'
        }`}
      >
        <legend className="text-[12px] font-pt-sans text-gray-100 uppercase tracking-[0.04em] px-[5px]">
          Conversion
        </legend>

        <div className="grid grid-cols-3 gap-[20px]">
          <div>
            <span className="block text-[16px] font-pt-sans font-bold text-black mb-[5px]">
              From Currency{!isEditMode && ' *'}
            </span>
            {isEditMode ? (
              <span className="block text-[15px] font-pt-sans text-black mt-[10px]">{fromCurrency}</span>
            ) : (
              <>
                <Multiselect
                  options={currencyOptions}
                  selectedValues={fromCurrency ? [fromCurrency] : []}
                  onUpdate={handleFromCurrencyChange}
                  isSingleSelectMode
                  hideCheckboxes
                  isCloseOnSelect
                  isEnableFuzzySearch
                  placeholderText="Select ..."
                  itemsClassname="bg-white"
                  borderColourClass={errors.fromCurrency ? 'border-red-95' : 'border-gray-40'}
                  primaryCtaClassName={errors.fromCurrency ? 'bg-red-25' : ''}
                />
                {errors.fromCurrency && (
                  <span className="block text-[13px] font-pt-sans text-red-95 mt-[4px]">{errors.fromCurrency}</span>
                )}
              </>
            )}
          </div>

          <div>
            <span className="block text-[16px] font-pt-sans font-bold text-black mb-[5px]">
              To Currency{!isEditMode && ' *'}
            </span>
            {isEditMode ? (
              <span className="block text-[15px] font-pt-sans text-black mt-[10px]">{toCurrency}</span>
            ) : (
              <>
                <Multiselect
                  options={currencyOptions}
                  selectedValues={toCurrency ? [toCurrency] : []}
                  onUpdate={handleToCurrencyChange}
                  isSingleSelectMode
                  hideCheckboxes
                  isCloseOnSelect
                  isEnableFuzzySearch
                  placeholderText="Select ..."
                  itemsClassname="bg-white"
                  borderColourClass={errors.toCurrency || sameCurrencyError ? 'border-red-95' : 'border-gray-40'}
                  primaryCtaClassName={errors.toCurrency || sameCurrencyError ? 'bg-red-25' : ''}
                />
                {errors.toCurrency && (
                  <span className="block text-[13px] font-pt-sans text-red-95 mt-[4px]">{errors.toCurrency}</span>
                )}
                {sameCurrencyError && !errors.toCurrency && (
                  <span className="block text-[13px] font-pt-sans text-red-95 mt-[4px]">
                    Currencies should be different
                  </span>
                )}
              </>
            )}
          </div>

          <div>
            <span className="block text-[16px] font-pt-sans font-bold text-black mb-[5px]">API Rate</span>
            <div className="flex items-center gap-[5px] bg-blue-10 rounded-[3px] px-[8px] h-[37px] min-w-0">
              <ActivityIcon className="w-[18px] h-[18px] text-status-enquiry flex-shrink-0" />
              <span className="text-[13px] font-pt-sans text-status-enquiry truncate">
                {bothCurrenciesSelected && !sameCurrencyError && apiRate != null ? apiRate : '---'}
              </span>
            </div>
          </div>
        </div>
      </fieldset>

      {/* SUPPLIER COST (BUY) Section */}
      <StrategySection
        title="Supplier Cost (Buy)"
        strategyLabel="Buy Strategy"
        strategy={buyStrategy}
        strategyError={errors.buyStrategy}
        value={buyValue}
        valueError={errors.buyValue}
        effectiveRate={buyEffectiveRate}
        onStrategyChange={handleBuyStrategyChange}
        onValueChange={val => {
          setBuyValue(val);
          setErrors(prev => ({ ...prev, buyValue: undefined }));
        }}
      />

      {/* PARTNER PRICE (SELL) Section */}
      <StrategySection
        title="Partner Price (Sell)"
        strategyLabel="Sell Strategy"
        strategy={sellStrategy}
        strategyError={errors.sellStrategy}
        value={sellValue}
        valueError={errors.sellValue}
        effectiveRate={sellEffectiveRate}
        onStrategyChange={handleSellStrategyChange}
        onValueChange={val => {
          setSellValue(val);
          setErrors(prev => ({ ...prev, sellValue: undefined }));
        }}
      />

      <div className="mt-[25px] flex items-center gap-[10px]">
        {isEditMode && (
          <FluidButton type="secondary" onClick={onClose}>
            Cancel
          </FluidButton>
        )}
        <FluidButton type="primary" onClick={handleSave} isLoading={activeMutation.isPending}>
          Save
        </FluidButton>
      </div>
    </GeneralModal>
  );
};

interface IStrategySectionProps {
  title: string;
  strategyLabel: string;
  strategy: EConversionStrategy | null;
  strategyError?: string;
  value: string;
  valueError?: string;
  effectiveRate: string;
  onStrategyChange: (values: string[]) => void;
  onValueChange: (value: string) => void;
}

const NUMERIC_REGEX = /^-?[0-9]*\.?[0-9]*$/;

const StrategySection = ({
  title,
  strategyLabel,
  strategy,
  strategyError,
  value,
  valueError,
  effectiveRate,
  onStrategyChange,
  onValueChange,
}: IStrategySectionProps) => {
  const isValueDisabled = !strategy || strategy === 'api_rate';
  const isMarkup = strategy === 'markup_percentage';

  const handleValueChange = (newValue: string) => {
    if (newValue !== '' && !NUMERIC_REGEX.test(newValue)) return;
    onValueChange(newValue);
  };

  const handleValueBlur = () => {
    if (value === '-' || value === '.' || value === '-.') {
      onValueChange('');
    }
  };

  return (
    <fieldset className="border border-gray-20 rounded px-[15px] pt-[10px] pb-[20px] mt-[25px]">
      <legend className="text-[12px] font-pt-sans text-gray-100 uppercase tracking-[0.04em] px-[5px]">{title}</legend>

      <div className="grid grid-cols-3 gap-[20px]">
        <div>
          <span className="block text-[16px] font-pt-sans font-bold text-black mb-[5px]">{strategyLabel} *</span>
          <Multiselect
            options={strategyOptions}
            selectedValues={strategy ? [strategy] : []}
            onUpdate={onStrategyChange}
            isSingleSelectMode
            hideCheckboxes
            isCloseOnSelect
            placeholderText="Select strategy"
            itemsClassname="bg-white"
            borderColourClass={strategyError ? 'border-red-95' : 'border-gray-40'}
            primaryCtaClassName={strategyError ? 'bg-red-25' : ''}
          />
          {strategyError && (
            <span className="block text-[13px] font-pt-sans text-red-95 mt-[4px]">{strategyError}</span>
          )}
        </div>

        <div>
          <span className="block text-[16px] font-pt-sans font-bold text-black mb-[5px]">
            Value {!isValueDisabled && '*'}
          </span>
          <div className="relative">
            <TextInput
              id="strategy-value"
              value={isValueDisabled ? '---' : value}
              onChange={e => handleValueChange(e.currentTarget.value)}
              disabled={isValueDisabled}
              placeholder={isValueDisabled ? '---' : 'Enter value'}
              className={isValueDisabled ? 'opacity-50' : ''}
              inputClassName={`!mt-0 ${valueError ? '!border-red-95 !bg-red-25' : ''} ${isMarkup ? '!pr-[25px]' : ''}`}
              autoComplete="off"
              onBlur={handleValueBlur}
            />
            {isMarkup && !isValueDisabled && (
              <span className="absolute right-[10px] top-1/2 -translate-y-1/2 text-[14px] text-gray-100">%</span>
            )}
          </div>
          {valueError && <span className="block text-[13px] font-pt-sans text-red-95 mt-[4px]">{valueError}</span>}
        </div>

        <div>
          <span className="block text-[16px] font-pt-sans font-bold text-black mb-[5px]">Effective Rate</span>
          <span className="block text-[15px] font-pt-sans text-black mt-[10px] truncate">{effectiveRate}</span>
        </div>
      </div>
    </fieldset>
  );
};
